import PropTypes from "prop-types";
import styles from "./Transaction.module.css";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import config from "../../config/config";
import {
  Autocomplete,
  Box,
  FormControl,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import {
  DataGrid,
  useGridApiRef,
  GridPreferencePanelsValue,
  getGridStringOperators,
  getGridSingleSelectOperators,
} from "@mui/x-data-grid";
import store from "../../Store/Store";
import { observer } from "mobx-react";
import ViewTransaction from "./ViewTransaction/ViewTransaction";
import FilterDate from "./FilterDate/FilterDate";
import PreviewImage from "./PreviewImage/PreviewImage";
import { useNavigate } from "react-router";
import NewUserPopup from "./NewUserPopup/NewUserPopup";
import companyStore from "../../Store/Store";
import { jwtDecode } from "jwt-decode";
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { useDebouncedLog } from "../../useDebouncedLog";
import { verifyTokenAccess } from "../../Helper";


const TXN_STATUS_LABEL = {
  processed: "PROCESSED",
  failed: "REVERSED",
};

const txnStatusLabel = (status) => TXN_STATUS_LABEL[status] || "PROCESSING";

/* ------------------------------------------------------------------
 * Typography.
 * --font-sarabun is declared on :root in global.css. Referencing the
 * variable (rather than hardcoding "Sarabun") keeps this in step with
 * the rest of the admin if the token is ever repointed.
 * ------------------------------------------------------------------ */
const SARABUN = "var(--font-sarabun), sans-serif";
const SANSOURCE = "var( --font-source-sans-3), sans-serif";

// Body / cell text colour, matching --color-dimgray-300.
const HEADER_COLOR = "#666666";
const TEXT_COLOR = "#4C4E64";
// Accent used for checked states and the panel's text buttons.
const ACCENT_COLOR = "#3246A9";
const MUTED_COLOR = "#8C8C8C";

/* ------------------------------------------------------------------
 * Grid sizing.
 * The grid is no longer autoHeight: autoHeight renders every row and
 * ignores the container, which pushed the pagination footer below the
 * fold. Instead the wrapper gets a definite height that grows with the
 * rows on the page and stops at 96vh — past that the grid scrolls its
 * own body and the header + footer stay put.
 * ------------------------------------------------------------------ */
const GRID_ROW_HEIGHT = 74;
const GRID_HEADER_HEIGHT = 56; // default columnHeaderHeight
const GRID_FOOTER_HEIGHT = 53; // pagination row
const GRID_MAX_HEIGHT = "96vh";
// rows to reserve space for when the grid is empty, so the no-rows /
// all-columns-hidden overlays have room
const GRID_MIN_ROWS = 3;

// Height of the status/approval badges inside their cells.
const BADGE_HEIGHT = 40;

// Text columns get exactly one operator, so the operator dropdown can be
// hidden and the panel shows nothing but a text box.
const CONTAINS_ONLY = getGridStringOperators().filter(
  (operator) => operator.value === "contains",
);

// Single-select columns get "is", which renders a value dropdown instead.
const IS_ONLY = getGridSingleSelectOperators().filter(
  (operator) => operator.value === "is",
);

// Same parser the built-in GridToolbarQuickFilter uses.
const parseQuickFilter = (input) =>
  input.split(" ").filter((word) => word !== "");

const EMPTY_FILTER_MODEL = {
  items: [],
  quickFilterValues: [],
  // hidden columns (Txn ID, Txn Note, Category, GL-Code) stay searchable
  quickFilterExcludeHiddenColumns: false,
};

// Columns hidden on first load. Lives here rather than in initialState
// because columnVisibilityModel is controlled — a controlled prop wins over
// initialState, so initialState would silently do nothing.
const DEFAULT_HIDDEN_COLUMNS = {
  transaction_id: false,
  __txnNote: false,
  __category: false,
  __glCode: false,
};

/* ------------------------------------------------------------------
 * Panel typography.
 * Both the columns panel and the filter panel render through the
 * `panel` slot, which is a Popper portaled to <body>. Nothing in the
 * grid's own sx or in .pageContainer reaches them, so the font and
 * colour have to be re-stated here. GridPanel wraps both panels, so
 * this one block covers the columns list and the filter form alike.
 * ------------------------------------------------------------------ */
const PANEL_SX = {
  // catch-all for nodes MUI doesn't expose a stable class for
  "& , & *": {
    fontFamily: SANSOURCE,
  },
  "& .MuiDataGrid-panelWrapper": {
    fontFamily: SANSOURCE,
  },
  // column rows: "DATE", "PAID TO", ...
  "& .MuiFormControlLabel-label, & .MuiTypography-root": {
    fontFamily: SANSOURCE,
    fontSize: 14,
    fontWeight: 500,
    color: TEXT_COLOR,
    letterSpacing: 0,
    textTransform: "none",
  },
  // the panel's own search box and the filter form inputs
  "& .MuiInputBase-root, & .MuiInputBase-input, & .MuiFormLabel-root, & .MuiSelect-select":
  {
    fontFamily: SANSOURCE,
    fontSize: 14,
    color: TEXT_COLOR,
  },
  "& .MuiInputBase-input::placeholder": {
    fontFamily: SANSOURCE,
    color: MUTED_COLOR,
    opacity: 1,
  },
  // Show/Hide All + RESET footer
  "& .MuiDataGrid-panelFooter .MuiButton-root": {
    fontFamily: SANSOURCE,
    fontSize: 13,
    fontWeight: 600,
    textTransform: "none",
    color: ACCENT_COLOR,
  },
  // checkboxes, otherwise MUI's default blue
  "& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate":
  {
    color: ACCENT_COLOR,
  },
  "& .MuiPaper-root, & .MuiDataGrid-paper": {
    borderRadius: "var(--br-xs)",   // 12px, matches your inputs
    overflow: "hidden",
  },
  "& .MuiDataGrid-panelWrapper": {
    borderRadius: "var(--br-xs)",
    overflow: "hidden",

  },
};

// Shared badge geometry for the two status columns: fill the cell edge
// to edge, keep a fixed height so the pill never stretches to the row.
const BADGE_SX = {
  width: "100%",
  maxWidth: "none",
  minWidth: 0,
  margin: 0,
  height: BADGE_HEIGHT,
  maxHeight: BADGE_HEIGHT,
  minHeight: 0,
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  fontFamily: SANSOURCE,
  lineHeight: 1.2,
};

const Transaction = observer(({ className = "" }) => {
  const navigate = useNavigate();
  const apiRef = useGridApiRef();

  const [data, setData] = useState([]);
  const [WalletInfo, setWalletinfo] = useState("Loading...");
  const [filterInput, setFilterInput] = useState("");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [monthlyLimit, setMonthlyLimit] = useState();
  const [availableLimit, setAvailableLimit] = useState();
  const [selectedCompany, setCompany] = useState("");
  const [filteron, setFilterOn] = useState(false);
  const [TransactionData, setTransactionData] = useState([]);
  const [years, setYears] = useState([]);
  const [months, setMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [finalSelectedMonth, finalSetSelectedMonth] = useState("");
  const [finalSelectedYear, finalSetSelectedYear] = useState("");
  const [invalidfilter, setinvalidfilter] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const [flag, setFlag] = useState(false);
  const [name, setName] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedEmailData, setSelectedEmailData] = useState({});
  const [VerifyselectedEmailData, setVerifySelectedEmailData] = useState({});
  const [filteredData, setFilteredData] = useState([]);
  const [noDataForTheUser, setNoDataForTheUser] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [imageName, setImageName] = useState("");
  const [category, setCategory] = useState([]);
  const [hideNote, setHidenote] = useState(false);
  const [advKycStatus, setAdvKycStatus] = useState("");
  const [showBanner, setShowBanner] = useState(false);
  const [whitelistingStatus, setWhitelistingStatus] = useState("");
  const [onboardingStatus, setOnboardingStatus] = useState("");
  const [dataFetched, setDataFetched] = useState(false)
  const [msgMonth, setMsgMonth] = useState(false)
  const [msgYear, setMsgYear] = useState(false)
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    DEFAULT_HIDDEN_COLUMNS,
  );

  const [AllUserTxn, setAllUserTxn] = useState(false)
  // --- DataGrid state (every control lives in the toolbar row) ---
  const [searchText, setSearchText] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterModel, setFilterModel] = useState(EMPTY_FILTER_MODEL);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  // both the filter panel and the columns panel anchor to whichever
  // toolbar button opened them
  const [panelAnchorEl, setPanelAnchorEl] = useState(null);
  // The columns panel always anchors here, whatever opened it.
  const columnsButtonRef = useRef(null);

  var logPageVisited; //Used
  var logTransactionFetched; //Used
  var logUserFetchedExpense; //Used
  var logCompanyDetailFetched; //Used
  var logDownloadReport; //Used
  var logExpenseFilter; //Used
  var logTransactionViewed; //Used
  var logRedirectToMoneyLoad;
  try {
    logPageVisited = useDebouncedLog("admin_expense_page_visited", 500);
    logTransactionFetched = useDebouncedLog("admin_expense_fetched", 500);
    logUserFetchedExpense = useDebouncedLog("admin_users_fetched_expense", 500);
    logCompanyDetailFetched = useDebouncedLog(
      "admin_company_detail_fetched",
      500,
    );
    logDownloadReport = useDebouncedLog("admin_expense_download_report", 500);
    logExpenseFilter = useDebouncedLog("admin_expense_filter_clicked", 500);
    logTransactionViewed = useDebouncedLog(
      "admin_transaction_vieweadmin_d_clicked",
    );
    logRedirectToMoneyLoad = useDebouncedLog("admin_redirect_to_money_load");
  } catch (err) {
    console.log("error", err);
  }

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token);

      if (decoded && decoded.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        return decoded.exp < currentTime;
      }
    } catch (error) {
      console.log("Error decoding token:", error);
      return true;
    }
    return true;
  };

  /* ----------------------------------------------------------------
   * Token refresh.
   * Returns the new tokens instead of toggling store.refresh. Toggling
   * refresh re-ran the bootstrap effect below, which clears the selected
   * user / rows / grid filters — that was what wiped the filters whenever
   * handleApply happened to run against an expired ID token.
   * ---------------------------------------------------------------- */
  const refreshTokens = async (authData) => {
    if (!authData?.RefreshToken) {
      localStorage.removeItem("authToken");
      navigate("/login");
      return null;
    }

    try {
      const client = new CognitoIdentityProviderClient({
        region: "ap-south-1",
      });

      const params = {
        ClientId: config.Client_Id,
        AuthFlow: "REFRESH_TOKEN_AUTH",
        AuthParameters: {
          REFRESH_TOKEN: authData?.RefreshToken,
        },
      };

      const command = new InitiateAuthCommand(params);
      const refreshResponse = await client.send(command);

      if (refreshResponse.AuthenticationResult) {
        const newAuthToken = {
          AccessToken: refreshResponse.AuthenticationResult.AccessToken,
          RefreshToken: authData?.RefreshToken,
          IdToken: refreshResponse.AuthenticationResult.IdToken,
        };

        localStorage.setItem("authToken", JSON.stringify(newAuthToken));
        return newAuthToken;
      }

      localStorage.removeItem("authToken");
      navigate("/login");
      return null;
    } catch (error) {
      console.log("Error refreshing tokens:", error);
      localStorage.removeItem("authToken");
      navigate("/login");
      return null;
    }
  };

  // Single place that hands out usable tokens. Awaited everywhere, so no
  // request goes out with a Promise in place of the token object.
  const getValidTokens = async () => {
    const tokens = localStorage.getItem("authToken");
    await verifyTokenAccess("Transaction Get Valid Token")
    if (!tokens) {
      console.log("no auth");
      navigate("/login");
      return null;
    }

    let parsedTokens;
    try {
      parsedTokens = JSON.parse(tokens);
    } catch (err) {
      console.log("Error parsing authToken", err);
      localStorage.removeItem("authToken");
      navigate("/login");
      return null;
    }

    if (isTokenExpired(parsedTokens.IdToken)) {
      return await refreshTokens(parsedTokens);
    }
    return parsedTokens;
  };
  const SELECT_MENU_SX = {
    borderRadius: "var(--br-xs)",
    "& .MuiMenuItem-root": {
      fontFamily: SARABUN,
      fontSize: 14,
      fontWeight: 500,
      color: TEXT_COLOR,
      letterSpacing: 0,
      textTransform: "none",
    },
    "& .MuiMenuItem-root.Mui-selected": {
      color: ACCENT_COLOR,
      backgroundColor: "rgba(50, 70, 169, 0.08)",
    },
    "& .MuiMenuItem-root.Mui-selected:hover": {
      backgroundColor: "rgba(50, 70, 169, 0.12)",
    },
  };
  useEffect(() => {
    if (!store.Company_Id) return;

    const fetchStatus = async () => {
      try {
        const parsedTokens = await getValidTokens();
        if (!parsedTokens) return;
        const res = await fetch(
          `${config.kyc_api_endpoint}/user/status?company_id=${store.Company_Id}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              aws_cognito_id_token: parsedTokens.IdToken,
              aws_cognito_access_token: parsedTokens.AccessToken,
            },
            body: JSON.stringify({
              company_id: store.Company_Id,
              kyc_company_id: store.KycCompanyId,
            }),
          },
        );

        const data = await res.json();
        const statusMap = Object.fromEntries(
          data.status.map((s) => [s.key, s.value]),
        );

        setAdvKycStatus(statusMap["advanced-kyc"]);
        setWhitelistingStatus(statusMap["whitelisting"]);
        setOnboardingStatus(data.onboarding_status);
        store.setCompTempId(data.company_id);
        store.setKycCompanyId(data?.company_id);
        store.setTradeName(data.trade_name);
        if (!res.ok || !data.success) {
          throw new Error(data.error || "Failed to fetch status");
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          // swallow
        }
      }
    };

    fetchStatus();
  }, [store.company_temp_id, store.refresh]);

  const resetGridFilters = () => {
    setSearchText("");
    setSearchOpen(false);
    setFilterModel(EMPTY_FILTER_MODEL);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  /* ----------------------------------------------------------------
   * Clearing the current selection is keyed on the company, not on
   * store.refresh. store.refresh is also flipped by unrelated flows, and
   * every flip used to blow away the selected user, the fetched rows and
   * (once resetGridFilters is re-enabled) the grid filters.
   * ---------------------------------------------------------------- */
  useEffect(() => {
    setSelectedEmailData({});
    setData([]);
    setTransactionData([]);
    setFilteredData([]);
    setSelectedEmail("");
    setFilterInput("");
    setName("");
    setSelectedUser(null);
    setAvailableLimit();
    setMonthlyLimit();
    // resetGridFilters();
  }, [store.Company_Id]);

  useEffect(() => {
    const bootstrap = async () => {
      const obj = {
        ledger_label: store.Company_Id,
      };

      const parsedTokens = await getValidTokens();
      if (!parsedTokens) return;

      try {
        let payload = {
          method: "post",
          maxBodyLength: Infinity,
          url: `${config.node_api_endpoint}/cupi/admin/get_cupi_compnay_users`,
          headers: {
            aws_cognito_id_token: parsedTokens.IdToken,
            aws_cognito_access_token: parsedTokens.AccessToken,
            "Content-Type": "application/json",
          },
          data: obj,
        };
        try {
          logUserFetchedExpense();
        } catch (err) {
          console.log("error%o", err);
        }
        axios
          .request(payload)
          .then(async (res) => {
            let newData = res.data.data
            newData.unshift({ mobile_no: "", name: "All Users", create_at: "ALL", user_id: "ALL" })
            setData(newData);

            const user = store.selectedExpenseMobileno;
            if (user !== "") {
              const userData = res.data.data.find(
                (item) => item.mobile_no === user,
              );
              setMonthlyLimit(userData.monthlyLimit);
              setSelectedEmailData(userData);
              setSelectedUser(userData);
            }
            const allOption = {
              mobile_no: "",
              name: "ALL USERS",
              create_at: "ALL",
              user_id: "ALL",
            };
            if (!store.selectedExpenseMobileno) {
              // setSelectedUser(allOption);
              // setFilterInput("ALL (ALL)");          // or just "ALL" if you prefer
              // setSelectedEmailData(allOption);
            }
          })
          .catch((er) => {
            console.log(er);
          });

        const currentYear = new Date().getFullYear();

        const yearOptions = [];
        for (let year = currentYear; year >= 2024; year--) {
          yearOptions.push(year);
        }
        setYears(yearOptions);
        setMonths(monthNames);
        if (store.selectedExpenseMobileno && selectedYear && selectedEmailData && selectedMonth) {
          handleApply();
        }
      } catch (err) {
        console.log("error in Getting Cupi users-->", err);
      }

      try {
        let payload = {
          method: "post",
          maxBodyLength: Infinity,
          url: `${config.node_api_endpoint}/cupi/admin/get_cupi_company_detail`,
          headers: {
            aws_cognito_id_token: parsedTokens.IdToken,
            aws_cognito_access_token: parsedTokens.AccessToken,
            "Content-Type": "application/json",
          },
          data: obj,
        };

        try {
          logCompanyDetailFetched();
        } catch (err) {
          console.log("error%o", err);
        }
        axios
          .request(payload)
          .then((res) => {
            setWalletinfo(res.data.data);
            store.setInsightAvilable(res.data.data.metabase_account_created);
          })
          .catch((er) => {
            console.log("error-->", er);
          });
        setCompany(store.companyName);

        setSelectedMonth(store.selectedMonth);
        setSelectedYear(store.selectedYear);
        setSelectedEmail(store.selectedEmail);
      } catch (err) {
        console.log("Error in walletInfo", err);
      }

      getEnabledCategories();
    };

    bootstrap();
  }, [store.refresh]);

  // Update month options based on the selected year
  useEffect(() => {
    let auth = localStorage.getItem("authToken");
    if (!auth) {
      console.log("no auth");
      navigate("/login");
      return;
    }

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    if (selectedYear === currentYear.toString()) {
      setMonths(monthNames.slice(0, currentMonth + 1));
    } else {
      setMonths(monthNames);
    }

    if (selectedYear == currentYear) {
      const month = monthNames.slice(0, currentMonth + 1);
      setSelectedMonth(month[month.length - 1]);
    } else {
      setSelectedMonth("");
    }
  }, [selectedYear]);

  // Date range filtering (FilterDate overlay) -> filteredData. Logic unchanged.
  useEffect(() => {
    let updatedFilteredData = [...TransactionData];

    if (store.minDate !== "" || store.maxDate !== "") {
      updatedFilteredData = updatedFilteredData.filter((transaction) => {
        const [datePart, timePart] = transaction.date.split(" ");
        const [day, month, year] = datePart.split("-");
        const [hours, minutes, seconds] = timePart.split(":");
        const txDate = new Date(
          `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`,
        );

        const startDate = store.minDate ? new Date(store.minDate) : null;
        const endDate = store.maxDate ? new Date(store.maxDate) : null;

        if (endDate) endDate.setHours(23, 59, 59, 999);

        const afterStart = startDate ? txDate >= startDate : true;
        const beforeEnd = endDate ? txDate <= endDate : true;

        return afterStart && beforeEnd;
      }, []);
    }

    setFilteredData(updatedFilteredData);
  }, [TransactionData, store.minDate, store.maxDate]);

  useEffect(() => {
    logPageVisited();
  }, [logPageVisited]);

  /* ----------------------------------------------------------------
   * Search -> MUI quick filter.
   * The grid owns the filtering; this just hands it the parsed values,
   * debounced the way the built-in quick filter does.
   * ---------------------------------------------------------------- */
  useEffect(() => {
    const timeout = setTimeout(() => {
      apiRef.current?.setQuickFilterValues(parseQuickFilter(searchText.trim()));
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 250);

    return () => clearTimeout(timeout);
  }, [searchText]);

  const resetDateFilter = () => {
    store.setMinDate("");
    store.setMaxDate("");
  };

  //Function to get the transactions data
  const handleApply = async () => {
    // resetGridFilters()
    // resetDateFilter()
    const monthIndex =
      monthNames.findIndex((month) => month === selectedMonth) + 1;
    const formattedMonthIndex = String(monthIndex).padStart(2, "0");
    setDataFetched(true)
    if (
      selectedYear == "" ||
      !selectedEmailData ||
      selectedEmailData == {} ||
      selectedMonth == "" ||
      !selectedEmailData.user_id
    ) {
      setinvalidfilter(true);
      return;
    }
    setMsgMonth(selectedMonth)
    setMsgYear(selectedYear)
    setVerifySelectedEmailData(selectedEmailData)
    setHidenote(true);
    setinvalidfilter(false);
    store.setSelectedMobile_no(selectedEmailData.mobile_no);
    store.setSelectedEmail(selectedEmailData.email);
    store.setSelectedMonth(selectedMonth);
    store.setSelectedYear(selectedYear);

    if (selectedEmailData == {} || selectedMonth == "" || selectedYear == "") {
      return;
    } else {
      const parsedTokens = await getValidTokens();
      if (!parsedTokens) return;

      const obj = JSON.stringify({
        user_id: selectedEmailData.user_id,
        user_create_at: selectedEmailData?.create_at,
        ledger_label: store.Company_Id,
        year: selectedYear,
        month: formattedMonthIndex,
      });
      let payload = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${config.node_api_endpoint}/cupi/admin/get_cupi_company_user_transactions`,
        headers: {
          aws_cognito_id_token: parsedTokens.IdToken,
          aws_cognito_access_token: parsedTokens.AccessToken,
          "Content-Type": "application/json",
        },
        data: obj,
      };
      try {
        logTransactionFetched();
      } catch (err) {
        console.log("error%o", err);
      }
      axios
        .request(payload)
        .then((res) => {
          let availableLimit = 0;

          setName(selectedEmailData?.name?.toUpperCase());

          // Filter out transactions where user_mobile_no is "NA"
          // or transaction_id contains "CUPILOAD"
          const filteredTransactions = (res?.data?.data || []).filter(
            (transaction) =>
              transaction.user_mobile_no !== "NA" &&
              !transaction.transaction_id?.includes("CUPILOAD")
          );

          filteredTransactions.forEach((transaction) => {
            if (transaction.status === "processed") {
              availableLimit += transaction.amount;
            }
          });

          let NewData =
            selectedEmailData.user_id == "ALL"
              ? [...filteredTransactions].reverse()
              : filteredTransactions;

          setAvailableLimit(availableLimit);
          setTransactionData(NewData);
          setFilteredData(NewData);

          if (selectedEmailData.user_id == "ALL") {
            setAllUserTxn(true);
          } else {
            setAllUserTxn(false);
          }

          // fresh fetch: old search / column filters no longer apply
          // resetGridFilters();

          if (NewData.length < 1) {
            setNoDataForTheUser(true);
          } else {
            setNoDataForTheUser(false);
          }
        })
        .catch((er) => {
          console.log("error-->", er);
          if (selectedEmailData.user_id == "ALL") {
            setAllUserTxn(true);
          } else {
            setAllUserTxn(false);
          }
        });
    }
    // store.setMinDate("");
    // store.setMaxDate("");
    finalSetSelectedMonth(selectedMonth);
    finalSetSelectedYear(selectedYear);
  };

  const handleResetApply = async () => {
    // resetGridFilters()
    // resetDateFilter()
    const monthIndex =
      monthNames.findIndex((month) => month === selectedMonth) + 1;
    const formattedMonthIndex = String(monthIndex).padStart(2, "0");
    setDataFetched(true)
    if (
      msgYear == "" ||
      !VerifyselectedEmailData ||
      VerifyselectedEmailData == {} ||
      msgMonth == "" ||
      !VerifyselectedEmailData.user_id
    ) {
      setinvalidfilter(true);
      return;
    }
    setHidenote(true);
    setinvalidfilter(false);
    store.setSelectedMobile_no(VerifyselectedEmailData.mobile_no);
    store.setSelectedEmail(VerifyselectedEmailData.email);
    store.setSelectedMonth(msgMonth);
    store.setSelectedYear(msgYear);

    if (VerifyselectedEmailData == {} || msgMonth == "" || msgYear == "") {
      return;
    } else {
      const parsedTokens = await getValidTokens();
      if (!parsedTokens) return;

      const obj = JSON.stringify({
        user_id: VerifyselectedEmailData?.user_id,
        user_create_at: VerifyselectedEmailData?.create_at,
        ledger_label: store.Company_Id,
        year: msgYear,
        month: formattedMonthIndex,
      });
      let payload = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${config.node_api_endpoint}/cupi/admin/get_cupi_company_user_transactions`,
        headers: {
          aws_cognito_id_token: parsedTokens.IdToken,
          aws_cognito_access_token: parsedTokens.AccessToken,
          "Content-Type": "application/json",
        },
        data: obj,
      };
      try {
        logTransactionFetched();
      } catch (err) {
        console.log("error%o", err);
      }
      setSelectedMonth(msgMonth)
      setSelectedYear(msgYear)
      setSelectedEmailData(VerifyselectedEmailData)
      setSelectedUser(VerifyselectedEmailData)
      axios
        .request(payload)
        .then((res) => {
          let availableLimit = 0;

          setName(selectedEmailData?.name?.toUpperCase());

          // Filter out transactions where user_mobile_no is "NA"
          // or transaction_id contains "CUPILOAD"
          const filteredTransactions = (res?.data?.data || []).filter(
            (transaction) =>
              transaction.user_mobile_no !== "NA" &&
              !transaction.transaction_id?.includes("CUPILOAD")
          );

          filteredTransactions.forEach((transaction) => {
            if (transaction.status === "processed") {
              availableLimit += transaction.amount;
            }
          });

          setAvailableLimit(availableLimit);
          setTransactionData(filteredTransactions);
          setFilteredData(filteredTransactions);

          if (VerifyselectedEmailData.user_id == "ALL") {
            setAllUserTxn(true);
          } else {
            setAllUserTxn(false);
          }

          // fresh fetch: old search / column filters no longer apply
          // resetGridFilters();

          if (filteredTransactions.length < 1) {
            setNoDataForTheUser(true);
          } else {
            setNoDataForTheUser(false);
          }
        })
        .catch((er) => {
          console.log("error-->", er);
          if (VerifyselectedEmailData.user_id == "ALL") {
            setAllUserTxn(true);
          } else {
            setAllUserTxn(false);
          }
        });
    }
    // store.setMinDate("");
    // store.setMaxDate("");
    finalSetSelectedMonth(selectedMonth);
    finalSetSelectedYear(selectedYear);
  };

  const handleYearChange = (event) => {
    setinvalidfilter(false);
    setSelectedYear(event.target.value);
    store.setYear(event.target.value);
  };

  const handleMonthChange = (event) => {
    setinvalidfilter(false);
    setSelectedMonth(event.target.value);
    store.setMonth(event.target.value);
  };

  const uniqueUsers = Array.from(
    new Set(
      data.map((item) =>
        JSON.stringify({ name: item.name, mobile_no: item.mobile_no }),
      ),
    ),
  ).map((item) => JSON.parse(item));

  const handleStore = (detail, category, index) => {
    try {
      logTransactionViewed();
    } catch (err) {
      console.log("error%o", err);
    }
    store.setDate(detail.date);
    store.setDebit(detail.amount);
    store.setPaidTo(detail.paid_to);
    store.setPurpose(detail.purpose);
    store.setUPIId(detail.upi_id);
    store.setUtr(detail.utr);
    store.setTxnId(detail.transaction_id);
    store.setStatus(detail.status);
    store.setCategory(category);
    store.setTxnStatus(detail.cupi_approval_status);
    store.setIndex(index);
    store.setResubmitReason(detail.cupi_approval_reason);
    store.setNewPurpose(detail?.transaction_note);
    store.setGl_code(detail?.categry_info?.gl_code);
  };

  // ---- CSV export (unchanged) ----
  const generateCSV = (data) => {
    if (!data || !data.length) return "";

    let headers = [
      "date",
      "purpose",
      "transaction_note",
      "amount",
      "paid_to",
      "upi_id",
      "utr",
      "transaction_id",
      "status",
      "cupi_approval_status",
      "cupi_approval_reason",
      "category",
    ];
    if (AllUserTxn) {

      headers = [
        "date",
        "user_name",
        "user_mobile_no",
        "purpose",
        "transaction_note",
        "amount",
        "paid_to",
        "upi_id",
        "utr",
        "transaction_id",
        "status",
        "cupi_approval_status",
        "cupi_approval_reason",
        "category",]
    }

    const transformedData = data.map((item) => {
      const categoryInfo =
        item.categry_info && typeof item.categry_info === "object"
          ? item.categry_info
          : item.category_info && typeof item.category_info === "object"
            ? item.category_info
            : {};

      const flat = {
        ...item,
        ...categoryInfo,
      };

      delete flat.categry_info;
      delete flat.category_info;

      if (flat.category_id !== undefined) {
        const categoryData = getcategoryByCategoryId(flat.category_id);
        flat.category = categoryData?.categoryName || "N/A";
        delete flat.category_id;
      } else {
        flat.category = flat.categoryName || "N/A";
      }

      flat.purpose =
        flat.purpose && flat.purpose !== "Expense" ? flat.purpose : "N/A";

      return flat;
    });

    const rows = transformedData.map((obj) =>
      headers
        .map((key) => {
          let value = obj[key];

          if (value === null || value === undefined) return "";

          value = String(value);

          if (
            value.includes(",") ||
            value.includes('"') ||
            value.includes("\n")
          ) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
        .join(","),
    );

    return [headers.join(","), ...rows].join("\n");
  };

  const downloadCSV = () => {
    if (!TransactionData || TransactionData.length === 0) {
      return;
    }

    try {
      logDownloadReport();
    } catch (err) {
      console.log("error%o", err);
    }

    const csvContent = generateCSV(TransactionData);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedEmailData.name}_(${selectedEmailData.mobile_no})_${selectedYear}_${selectedMonth}.csv`;
    if (AllUserTxn) {
      a.download = `All_Transactions_${msgYear}_${msgMonth}(${store.Company_Id}).csv`
    }
    a.click();
    URL.revokeObjectURL(url);
  };

  const getcategoryByCategoryId = (categoryId) => {
    if (!category || !Array.isArray(category)) {
      return null;
    }
    const foundCategory = category.find(
      (item) => item.categoryId === categoryId,
    );
    return foundCategory || null;
  };

  const getEnabledCategories = async () => {
    try {
      const parsedTokens = await getValidTokens();
      if (!parsedTokens) return;

      const Obj = {
        ledger_label: store.Company_Id,
      };

      let payload = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${config.node_api_endpoint}/cupi/admin/get_categories`,
        headers: {
          aws_cognito_id_token: parsedTokens.IdToken,
          aws_cognito_access_token: parsedTokens.AccessToken,
          "Content-Type": "application/json",
        },
        data: Obj,
      };
      axios
        .request(payload)
        .then((res) => {
          setCategory(res.data.data);
        })
        .catch((err) => {
          console.log("Error %o", err);
        });
    } catch (err) {
      console.log("error %o", err);
    }
  };

  const getStatusConfig = (TxnStatus, status) => {
    const isProcessed = status === "processed";
    if (status == "processed") {
      switch (TxnStatus) {
        case "approved":
          return isProcessed
            ? {
              text: "SPEND APPROVED",
              image: "/Check_Icon.svg",
              className: styles.dataApproved,
            }
            : {
              text: "NA",
              image: "/na.svg",
              className: styles.dataNA,
            };
        case "pending_receipt_upload":
          return {
            text: "YET TO SUBMIT BILL",
            image: "/na.svg",
            className: styles.dataNotSubmitted,
          };
        case "rejected":
          return {
            text: "SPEND REJECTED",
            image: "/X-Icon.svg",
            className: styles.dataRejected,
          };
        case "receipt_submitted_pending_admin_approval":
          return {
            text: "BILL SUBMITTED",
            image: "/hourGlass-icon.svg",
            className: styles.dataBillSubmitted,
          };
        case "pending_receipt_resubmit_requested":
          return {
            text: "RESUBMIT BILL",
            image: "/refresh.svg",
            className: styles.dataResubmit,
          };
        default:
          return {
            text: "NA",
            image: "/na.svg",
            className: styles.dataNA,
          };
      }
    } else {
      return {
        text: "NA",
        image: "/na.svg",
        className: styles.dataNA,
      };
    }
  };

  const renderStatus = (txnStatus, status, index) => {
    let newStatus = txnStatus;
    const { text, image, className } = getStatusConfig(newStatus, status);

    return (
      <div className={className}>
        {newStatus != "cupi_approval_status" &&
          newStatus != "NA" &&
          newStatus != "pending_receipt_upload" &&
          status === "processed" && (
            <img alt={text} src={image} className={styles.btn_img} />
          )}
        <span>{text}</span>
      </div>
    );
  };

  const GetS3Access = () => { };

  /* ----------------------------------------------------------------
   * Grid rows.
   * Display values are precomputed into their own fields so the filter
   * panel and the search box operate on what the user actually sees
   * ("BILL SUBMITTED", not "receipt_submitted_pending_admin_approval").
   * ---------------------------------------------------------------- */
  const gridRows = useMemo(
    () =>
      (filteredData || []).map((detail, index) => {
        const categoryData = getcategoryByCategoryId(detail?.category_id);
        return {
          ...detail,
          id: detail.transaction_id || detail.utr || `row-${index}`,
          __index: index,
          __date: detail?.date ? String(detail.date).split(" ")[0] : "",
          __purpose:
            !detail?.purpose ||
              detail.purpose === "N/A" ||
              detail.purpose === "Expense"
              ? "N/A"
              : detail.purpose,
          __amount: Number(detail?.amount || 0).toFixed(2),
          __txnNote: detail?.transaction_note || "",
          __category: categoryData?.categoryName || "N/A",
          __glCode: detail?.categry_info?.gl_code || "N/A",
          __txnStatus: txnStatusLabel(detail?.status),
          __approval: getStatusConfig(
            detail?.cupi_approval_status,
            detail?.status,
          ).text,
        };
      }),
    [filteredData, category],
  );

  // Dropdown options for the two single-select columns, taken from the
  // rows actually loaded so the list never offers an empty result.
  const txnStatusOptions = useMemo(
    () => [...new Set(gridRows.map((row) => row.__txnStatus))].sort(),
    [gridRows],
  );
  const approvalOptions = useMemo(
    () => [...new Set(gridRows.map((row) => row.__approval))].sort(),
    [gridRows],
  );

  // Rebuild the columns only when the option *contents* change. Every fetch
  // produces fresh arrays, and a new columns array makes the grid re-hydrate
  // its singleSelect columns — which drops any active filter item whose value
  // it can no longer match against valueOptions.
  const txnStatusKey = txnStatusOptions.join("|");
  const approvalKey = approvalOptions.join("|");

  const ellipsisCell = (value) => (
    <Tooltip title={value || ""}>
      <span
        style={{
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </span>
    </Tooltip>
  );

  const columns = useMemo(
    () => [
      {
        field: "__date",
        headerName: "DATE",
        flex: 0.8,
        minWidth: 90,
        headerAlign: "center",
        align: "center",
        filterOperators: CONTAINS_ONLY,
      },

      // ── only when AllUserTxn is true ──────────────────────────────
      ...(AllUserTxn
        ? [
          {
            field: "user_name",
            headerName: "USER NAME",
            flex: 1.2,
            minWidth: 120,
            filterOperators: CONTAINS_ONLY,
            renderCell: (params) => ellipsisCell(params.value),
          },
          {
            field: "user_mobile_no",
            headerName: "PHONE NUMBER",
            flex: 1.1,
            minWidth: 130,
            filterOperators: CONTAINS_ONLY,
            renderCell: (params) => ellipsisCell(params.value),
          },
        ]
        : []),

      {
        field: "__purpose",
        headerName: "PURPOSE",
        flex: 1.1,
        minWidth: 90,
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "paid_to",
        headerName: "PAID TO",
        flex: 1.2,
        minWidth: 100,
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "__amount",
        headerName: "AMOUNT PAID",
        flex: 1,
        minWidth: 100,
        headerAlign: "right",
        align: "right",
        filterOperators: CONTAINS_ONLY,
        sortComparator: (a, b) => Number(a) - Number(b),
      },
      {
        field: "upi_id",
        headerName: "UPI ID",
        flex: 1.3,
        minWidth: 110,
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "utr",
        headerName: "UTR/RRN",
        flex: 1.2,
        minWidth: 110,
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      // hidden by default — these keep fixed widths so un-hiding them
      // widens the grid (and scrolls) instead of squeezing everything else
      {
        field: "transaction_id",
        headerName: "TXN ID",
        width: 190,
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "__txnNote",
        headerName: "TXN NOTE",
        width: 160,
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "__category",
        headerName: "CATEGORY",
        width: 160,
        headerAlign: "start",
        align: "start",
        border: "1px solid red",
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "__glCode",
        headerName: "GL-CODE",
        width: 140,
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "__txnStatus",
        headerName: "TXN STATUS",
        flex: 1.1,
        minWidth: 110,
        headerAlign: "center",
        align: "center",
        type: "singleSelect",
        valueOptions: txnStatusKey ? txnStatusKey.split("|") : [],
        filterOperators: IS_ONLY,
        renderCell: (params) => (
          <div
            className={
              params.row.status === "processed"
                ? styles.tableBodydataStatusComp
                : params.row.status === "failed"
                  ? styles.tableBodydataStatusRever
                  : styles.tableBodydataStatus
            }
          >
            {params.value}
          </div>
        ),
      },
      {
        field: "__approval",
        headerName: "APPROVAL STATUS",
        flex: 1.5,
        minWidth: 190,
        headerAlign: "center",
        align: "center",
        type: "singleSelect",
        valueOptions: approvalKey ? approvalKey.split("|") : [],
        filterOperators: IS_ONLY,
        renderCell: (params) =>
          renderStatus(
            params.row.cupi_approval_status,
            params.row.status,
            params.row.__index,
          ),
      },
      {
        field: "__action",
        headerName: "ACTION",
        flex: 1.1,
        minWidth: 110,
        sortable: false,
        filterable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <button
            className={styles.button}
            onClick={() => {
              const categoryData = getcategoryByCategoryId(
                params.row.category_id,
              );
              handleStore(
                params.row,
                categoryData?.categoryName,
                params.row.__index,
              );
              setFlag(true);
            }}
          >
            <img alt="" src="/eye.svg" className={styles.btn_img} />
            View More
          </button>
        ),
      },
    ],
    [category, txnStatusKey, approvalKey, AllUserTxn], // ← added AllUserTxn
  );

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const toggleSearch = () => {
    setSearchOpen((open) => {
      if (open) {
        setSearchText(""); // closing clears the quick filter
      }
      return !open;
    });
  };

  const openFilterPanel = (event) => {
    setPanelAnchorEl(event.currentTarget);
    apiRef.current?.showFilterPanel();
  };

  const openColumnsPanel = (event) => {
    // Pinned to the toolbar icon so the panel lands in the same place whether
    // it was opened from the toolbar or from the all-columns-hidden state.
    setPanelAnchorEl(columnsButtonRef.current ?? event?.currentTarget ?? null);
    apiRef.current?.showPreferences(GridPreferencePanelsValue.columns);
  };

  const activeFilterCount = filterModel.items?.length || 0;

  /* ----------------------------------------------------------------
   * The grid has no "no visible columns" state of its own: hide every
   * column and it renders an empty shell with no overlay. Detect it here
   * and put a Manage Columns action in that space instead.
   * ---------------------------------------------------------------- */
  const allColumnsHidden = useMemo(
    () => columns.every((col) => columnVisibilityModel[col.field] === false),
    [columns, columnVisibilityModel],
  );

  /* ----------------------------------------------------------------
   * Wrapper height: grows with the rows on the current page, capped at
   * 96vh. gridRows is the pre-pagination row set, so this is an upper
   * bound — a page filtered down to fewer rows simply leaves a little
   * empty space, it never clips the footer.
   * ---------------------------------------------------------------- */
  const rowsOnPage =
    Math.min(gridRows.length, paginationModel.pageSize) || GRID_MIN_ROWS;
  const gridHeight = `min(${GRID_MAX_HEIGHT}, ${GRID_HEADER_HEIGHT + GRID_FOOTER_HEIGHT + rowsOnPage * GRID_ROW_HEIGHT
    }px)`;

  return (
    <div className={[styles.pageContainer, className].join(" ")}>
      <div className={styles.frameParent}>
        <div className={styles.frameGroup}>
          <div className={styles.split}>
            <div>
              <div className={styles.cards16pxWrapper}>
                <div className={styles.cards16px}>
                  <div className={styles.saveMoney1044644423Parent}>
                    <div className={styles.saveMoney1044644423}>
                      <img
                        className={styles.vectorIcon}
                        alt=""
                        src="/vector.svg"
                      />
                    </div>
                    <div className={styles.availableBalanceParent}>
                      <div className={styles.availableBalance}>
                        Available Balance*
                      </div>
                      <div className={WalletInfo?.balance_cr_dr?.toLowerCase() === "dr" ? styles.divRed : styles.div}>
                        {WalletInfo?.balance_cr_dr?.toLowerCase() === "dr"
                          ? `-₹${WalletInfo?.balance || "Loading..."}`
                          : `₹${WalletInfo?.balance || "Loading..."}`}
                      </div>
                    </div>
                  </div>

                  {store.Company_Id != "AxisDemo" && (
                    <div className={styles.saveMoney1044644423Parent1}>
                      <div className={styles.saveMoney1044644423row}>
                        <div className={styles.saveMoney1044644423}>
                          <img
                            className={styles.vectorIcon}
                            alt=""
                            src="/wallet.svg"
                          />
                        </div>
                        <div className={styles.availableBalanceParent1}>
                          <div className={styles.availableBalance}>
                            Account Details*
                            <div className={styles.tiptool}></div>
                          </div>
                          <div className={styles.div16}>
                            <div>Ac.no</div>
                            <div>:</div>
                            <div>{WalletInfo?.van?.acc_no || "Loading..."}</div>
                          </div>
                          <div className={styles.div16}>
                            <div>IFSC</div>
                            <div>:</div>
                            <div>{WalletInfo?.van?.ifsc || "Loading..."}</div>
                          </div>
                        </div>
                      </div>
                      {companyStore.userType != "AxisUser" && (
                        <div
                          className={styles.RedirectLink}
                          onClick={() => {
                            try {
                              logRedirectToMoneyLoad();
                            } catch (err) {
                              console.log("error%o", err);
                            }
                            navigate("/content/kycValidation?tab=2");
                          }}
                        >
                          How to load money? Click here
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {!showBanner && (
                  <NewUserPopup
                    advKycStatus={advKycStatus}
                    whitelistingStatus={whitelistingStatus}
                    onboardingStatus={onboardingStatus}
                    setShowBanner={setShowBanner}
                    showOthers={true}
                  />
                )}
              </div>
              {invalidfilter && (
                <div className={styles.noteconatiner}>
                  <span className={styles.divSpan}>
                    <span className={styles.divSpanBold}>Note:</span> Please select a user, year, and month to view transactions.
                  </span>
                </div>
              )}
              <div className={styles.filterContainer}>
                <div className={styles.filterOptions}>
                  <Autocomplete
                    disablePortal
                    options={uniqueUsers}
                    getOptionLabel={(option) =>
                      option ? `${option.name} ${option.mobile_no ? `(${option.mobile_no})` : ""}` : ""
                    }
                    filterOptions={(options, { inputValue }) => {
                      const input = inputValue.toLowerCase();
                      return options.filter(
                        (option) =>
                          option.name.toLowerCase().includes(input) ||
                          option.mobile_no.toLowerCase().includes(input),
                      );
                    }}
                    noOptionsText={
                      filterInput &&
                        !uniqueUsers.some(
                          (user) =>
                            user.name
                              .toLowerCase()
                              .includes(filterInput.toLowerCase()) ||
                            user.mobile_no
                              .toLowerCase()
                              .includes(filterInput.toLowerCase()),
                        )
                        ? "User not found"
                        : "No options"
                    }
                    onInputChange={(event, newInputValue) => {
                      setinvalidfilter(false);
                      setFilterInput(newInputValue);
                    }}
                    onChange={(event, newValue) => {
                      setinvalidfilter(false);
                      if (newValue) {
                        const selectedData = data.find(
                          (item) =>
                            item.mobile_no === newValue.mobile_no &&
                            item.name === newValue.name,
                        );
                        if (selectedData) {
                          store.setmonthlyLimit(selectedData.monthlyLimit);
                          setMonthlyLimit(selectedData.monthlyLimit);
                          setSelectedEmailData(selectedData);
                          setSelectedUser(selectedData);
                          setFilterInput(
                            `${selectedData.name} (${selectedData.mobile_no})`,
                          );
                        }
                      } else {
                        setSelectedEmailData({});
                        setSelectedUser(null);
                        setNoDataForTheUser(false);
                        setFilterInput("");
                      }
                      setinvalidfilter(false);
                    }}
                    // the options popup is portaled — restate the font there
                    slotProps={{
                      paper: {
                        sx: {
                          fontFamily: SANSOURCE,
                          "& .MuiAutocomplete-option, & .MuiAutocomplete-noOptions":
                          {
                            fontFamily: SANSOURCE,
                            fontSize: 16,
                            color: HEADER_COLOR,
                          },
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          width: "220px",
                          height: "56px",
                          "& .MuiInputBase-input": {
                            fontFamily: SANSOURCE,
                            fontSize: 16,
                          },
                          "& .MuiInputBase-input::placeholder": {
                            fontFamily: SANSOURCE,
                            color: HEADER_COLOR,
                            opacity: 1,
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          style: {
                            borderRadius: "12px",
                            height: "56px",
                            fontFamily: SANSOURCE,
                            border: invalidfilter ? "1px solid red" : "",
                            color: invalidfilter ? "red" : "",
                          },
                        }}
                        placeholder="Search by Name or Phone..."
                      />
                    )}
                    value={selectedUser}
                  />
                  <div className={styles.filteryear_month}>
                    <FormControl>
                      <Select
                        value={selectedYear}
                        onChange={handleYearChange}
                        displayEmpty
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              "& .MuiMenuItem-root": {
                                fontFamily: SARABUN,
                                fontSize: 15,
                                color: HEADER_COLOR,
                              },
                            },
                          },
                        }}
                        sx={{
                          width: "120px",
                          height: "56px",
                          border: invalidfilter ? "1px solid red" : "",
                          borderRadius: "10px",
                          fontFamily: SANSOURCE,
                          color: invalidfilter ? "red" : HEADER_COLOR,
                          fontSize: "16px",
                        }}
                      >
                        <MenuItem value="">Year</MenuItem>
                        {years.map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl>
                      <Select
                        value={selectedMonth}
                        onChange={handleMonthChange}
                        displayEmpty
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              "& .MuiMenuItem-root": {
                                fontFamily: SARABUN,
                                fontSize: 15,
                                color: HEADER_COLOR,
                              },
                            },
                          },
                        }}
                        sx={{
                          width: "120px",
                          height: "56px",
                          border: invalidfilter ? "1px solid red" : "",
                          borderRadius: "10px",
                          fontFamily: SARABUN,
                          color: invalidfilter ? "red" : HEADER_COLOR,
                          fontSize: "16px",
                        }}
                      >
                        <MenuItem value="">Month</MenuItem>
                        {months.map((month) => (
                          <MenuItem key={month} value={month}>
                            {month.toString().padStart(2, "0")}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <button
                    className={styles.Button}
                    onClick={() => {
                      handleApply();
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
            <div></div>
          </div>
          <div
            className={styles.feeStatementDataTable}
            style={{ minWidth: 0, maxWidth: "98%", overflow: "hidden" }}
          >
            <div className={styles.top}>
              <div className={styles.latestAccountTransactionParent}>
                <div className={styles.latestAccountTransaction}>
                  <span className={styles.ChnageColor}>
                    Account Transactions {!AllUserTxn && `:`}
                  </span>{" "}
                  {
                    (AllUserTxn && dataFetched) ? `` : name ? name.toUpperCase() : ""}
                </div>

                {/* all table controls live here — nothing in the column headers */}
                <div className={styles.filter}>
                  {/* search — hidden until the search button is pressed */}
                  {searchOpen && (
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Search transactions"
                      value={searchText}
                      onChange={handleSearchChange}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") toggleSearch();
                      }}
                      sx={{
                        minWidth: 220,
                        "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                        "& .MuiInputBase-input": {
                          fontFamily: SARABUN,
                          fontSize: 14,
                          color: TEXT_COLOR,
                        },
                        "& .MuiInputBase-input::placeholder": {
                          fontFamily: SARABUN,
                          color: MUTED_COLOR,
                          opacity: 1,
                        },
                      }}
                    />
                  )}

                  {/* search toggle */}
                  <button
                    className={styles.filterLinesParent}
                    onClick={toggleSearch}
                  >
                    <img
                      className={styles.filterLinesIcon}
                      alt=""
                      src="/SearchSVG.svg"
                    />
                  </button>

                  {/* date range filter — unchanged */}
                  <button
                    className={styles.filterLinesParent}
                    onClick={(e) => {
                      try {
                        logExpenseFilter();
                      } catch (err) {
                        console.log("error%o", err);
                      }
                      setFilterOn(true);
                      e.stopPropagation();
                    }}
                  >
                    <img
                      className={styles.filterLinesIcon}
                      alt=""
                      src="/DateIcon.svg"
                    />
                  </button>

                  {/* show / hide columns */}
                  <button
                    ref={columnsButtonRef}
                    className={styles.filterLinesParent}
                    onClick={openColumnsPanel}
                  >
                    <img
                      className={styles.filterLinesIcon}
                      alt=""
                      src="/ThreeHorizontalLine.svg"
                    />
                  </button>

                  {/* column filter — column + value, no operator */}
                  <button
                    className={styles.filterLinesParent}
                    onClick={openFilterPanel}
                  >
                    <img
                      className={styles.filterLinesIcon}
                      alt=""
                      src="/ThreeVerticalline.svg"
                    />
                  </button>

                  {filteron && (
                    <div
                      className={styles.newUsersOverlay1}
                      onClick={() => {
                        setFilterOn(false);
                      }}
                    >
                      <FilterDate
                        isVisible={filteron}
                        users={selectedUser}
                        onClose={() => {
                          setFilterOn(false);
                        }}
                        selectedMonth={finalSelectedMonth}
                        selectedYear={finalSelectedYear}
                      />
                    </div>
                  )}

                  {/* export — untouched */}
                  <button
                    className={styles.download01Parent}
                    onClick={() => {
                      downloadCSV();
                    }}
                  >
                    <img
                      className={styles.filterLinesIcon}
                      alt=""
                      src="/NewDownloadIcon.svg"
                    />
                  </button>

                  <button
                    className={styles.reLoadTable}
                    onClick={() => {
                      handleResetApply();
                      resetGridFilters();
                      resetDateFilter()
                      setPaginationModel({
                        page: 0,
                        pageSize: 10,
                      })
                      setColumnVisibilityModel({ ...DEFAULT_HIDDEN_COLUMNS });
                    }}
                  >
                    <img
                      className={styles.vectorIcon1}
                      alt=""
                      src="/vector7.svg"
                    />
                  </button>
                </div>
              </div>
              {AllUserTxn ? <div className={styles.latestAccountTransaction1}>{`Showing all user transactions for ${msgMonth} ${msgYear}`}</div> : <div className={styles.latestAccountTransaction1}>
                Total Monthly Spend :
                <div className={styles.allowedTransactions}>
                  {availableLimit && monthlyLimit
                    ? `  ${Number(availableLimit).toFixed(2)}/${monthlyLimit}`
                    : ""}
                </div>
              </div>}
            </div>
            <Box
              sx={{
                position: "relative",
                width: "calc(100vw - 330px)",
                minWidth: 0,
                // definite height, capped at 96vh — the grid scrolls its own
                // rows, so the header and the pagination footer stay visible
                height: gridHeight,
                ...(allColumnsHidden && { minHeight: 280 }),
                overflow: "hidden",
                fontFamily: SARABUN,
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                  borderBottom: "none",
                  overflow: "hidden",
                },
                "& .MuiDataGrid-footerContainer": { border: "none" },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
              }}
            >
              <DataGrid
                apiRef={apiRef}
                rows={gridRows}
                rowHeight={GRID_ROW_HEIGHT}
                columnHeaderHeight={GRID_HEADER_HEIGHT}
                columns={columns}
                disableColumnMenu
                disableRowSelectionOnClick
                filterModel={filterModel}
                onFilterModelChange={(model) => {
                  setFilterModel(model);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                slotProps={{
                  baseSelect: {
                    MenuProps: { PaperProps: { sx: SELECT_MENU_SX } },
                  },
                  // GridPanel wraps BOTH the columns panel and the filter
                  // panel, so PANEL_SX styles the pair of them.
                  panel: {
                    anchorEl: panelAnchorEl,
                    placement: "bottom-end",
                    sx: PANEL_SX,
                  },
                  filterPanel: {
                    filterFormProps: {
                      logicOperatorInputProps: { sx: { display: "none" } },
                      operatorInputProps: { sx: { display: "none" } },
                      columnInputProps: { sx: { minWidth: 160 } },
                      valueInputProps: {
                        InputComponentProps: { sx: { minWidth: 200 } },
                      },
                    },
                    sx: {
                      "& .MuiDataGrid-filterFormLogicOperatorInput": {
                        display: "none",
                      },
                      "& .MuiDataGrid-panelWrapper": { minWidth: 460 },
                      "& .MuiDataGrid-panelContent": { overflowX: "Scroll" },
                      "& .MuiDataGrid-filterForm": {
                        p: 2,
                        gap: 1,
                        alignItems: "flex-end",
                        flexWrap: "wrap",
                      },
                      "& .MuiDataGrid-filterFormDeleteIcon": {
                        flexShrink: 0,
                        alignSelf: "flex-end",
                      },
                    },
                  },
                }}
                localeText={{
                  noRowsLabel: noDataForTheUser
                    ? "No transactions found for this user."
                    : "Select the user phone number, year and month to get started.",
                  noResultsOverlayLabel:
                    "No transactions match this search or filter.",
                  filterPanelColumns: "Column",
                  filterPanelInputLabel: "Value",
                  filterPanelInputPlaceholder: "Filter value",
                }}
                sx={{
                  // fills the capped wrapper; the body scrolls, the footer doesn't
                  height: "100%",
                  width: "100%",
                  maxWidth: "100%",
                  border: "none",
                  fontFamily: SARABUN,
                  color: TEXT_COLOR,

                  // Sarabun through every part of the grid, including the
                  // nodes rendered by renderCell
                  "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-cell, & .MuiDataGrid-cell *, & .MuiDataGrid-overlay, & .MuiDataGrid-footerContainer, & .MuiTablePagination-root":
                  {
                    fontFamily: SARABUN,
                  },

                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f7f8fa",
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 600,
                    fontSize: 14,
                    letterSpacing: 0.4,
                    color: HEADER_COLOR,
                  },
                  "& .MuiDataGrid-columnSeparator": { display: "none" },
                  "& .MuiDataGrid-cell": {
                    display: "flex",
                    alignItems: "center", // stops the pills stretching
                    borderBottom: "none",
                    overflow: "hidden",
                    fontSize: 14,
                  },
                  "& .MuiDataGrid-footerContainer": { borderTop: "none" },
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                  {
                    outline: "none",
                  },

                  // the two badge columns: zero the cell gutter so the pill
                  // can reach the cell edges
                  "& .MuiDataGrid-cell[data-field='__txnStatus'], & .MuiDataGrid-cell[data-field='__approval']":
                  {
                    padding: "0 10"
                  },
                  "& .MuiDataGrid-cell[data-field='__approval']":
                  {
                    padding: "0 10",
                    minWidth: "175px",
                    maxWidth: "175px",
                  },
                  "& .MuiDataGrid-cell[data-field='__txnNote']":
                  {
                    padding: "0 10",
                    maxWidth: "160px",
                  },
                  "& .MuiDataGrid-cell[data-field='__glCode']":
                  {
                    padding: "0 10",
                    maxWidth: "160px",
                  },
                  "& .MuiDataGrid-cell[data-field='__category']":
                  {
                    padding: "0 10",
                    maxWidth: "160px",
                  },
                  "& .MuiDataGrid-cell[data-field='__txnStatus'] > *": BADGE_SX,
                  "& .MuiDataGrid-cell[data-field='__approval'] > *": BADGE_SX,
                }}
              />

              {/* every column hidden — the grid draws nothing, so offer the
                  way back instead of a blank table */}
              {allColumnsHidden && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                    backgroundColor: "#fff",
                    zIndex: 2,
                  }}
                >
                  <span
                    style={{
                      fontSize: 14,
                      color: "#667085",
                      fontFamily: SARABUN,
                    }}
                  >
                    All columns are hidden.
                  </span>
                  <button className={styles.Button} onClick={openColumnsPanel}>
                    Manage Columns
                  </button>
                </div>
              )}
            </Box>
          </div>
        </div>
      </div>

      {flag && (
        <div
          className={styles.newUsersOverlay}
          onClick={() => {
            setFlag(false);
            setShowImage(false);
          }}
        >
          <ViewTransaction
            isVisible={flag}
            onClose={() => {
              setFlag(false);
              handleApply();
            }}
            openImg={() => {
              setShowImage(true);
            }}
            closeImg={() => {
              setShowImage(false);
            }}
            setimg={(data) => {
              setSelectedImage(data);
            }}
            setName={(data) => {
              setImageName(data);
            }}
            setOpenSuccessPopup={() => { }}
            setOpenFailedPopup={() => { }}
          />
          <PreviewImage
            isVisible={flag && showImage}
            CloseImg={() => {
              setShowImage(false);
            }}
            image={selectedImage}
            name={imageName}
          />
        </div>
      )}
    </div>
  );
});

Transaction.propTypes = {
  className: PropTypes.string,
};

export default Transaction;
